import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui';
import { commsKeys, releaseNoteApi, type ReleaseNote } from '@/features/platform/api/comms';
import { useSession } from '@/lib/session';

const CATEGORY: Record<ReleaseNote['category'], { label: string; tone: 'info' | 'success' | 'neutral' | 'danger' }> = {
  feature: { label: 'New', tone: 'success' },
  improvement: { label: 'Improved', tone: 'info' },
  fix: { label: 'Fixed', tone: 'neutral' },
  security: { label: 'Security', tone: 'danger' },
};

/** Release note body as plain text with line breaks (no HTML). */
export function ReleaseNoteBody({ body }: { body: string }) {
  return <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{body}</p>;
}

export function ReleaseNoteCategory({ category }: { category: ReleaseNote['category'] }) {
  const c = CATEGORY[category] ?? CATEGORY.feature;
  return <Badge tone={c.tone}>{c.label}</Badge>;
}

/** Header shortcut to the existing updates page for each administration area. */
export function UpdatesPanel({ navigateTo }: { navigateTo?: (to: string) => void }) {
  const { user, principal, impersonation } = useSession();
  const platform = !impersonation && user?.platformRole === 'platform_operator';
  const allowed = !!user && (platform || (principal?.kind === 'staff' && ['tenant_owner', 'tenant_admin'].includes(principal.role)));
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const unread = useQuery({ queryKey: commsKeys.updatesUnread, queryFn: ({ signal }) => releaseNoteApi.unread(signal), enabled: allowed, refetchInterval: 10 * 60_000, staleTime: 60_000 });
  const seen = useMutation({ mutationFn: releaseNoteApi.markSeen, onSuccess: data => qc.setQueryData(commsKeys.updatesUnread, data) });
  const count = unread.data?.unread ?? 0;
  const destination = platform ? '/platform/announcements?view=updates' : '/updates';
  const onUpdatesPage = platform
    ? location.pathname === '/platform/announcements' && new URLSearchParams(location.search).get('view') === 'updates'
    : location.pathname === '/updates' && new URLSearchParams(location.search).get('tab') !== 'team';

  // Mark seen only after navigation succeeds (platform forms can block navigation).
  useEffect(() => {
    if (allowed && onUpdatesPage && count > 0 && !impersonation && !seen.isPending && !seen.isError) seen.mutate();
  }, [allowed, onUpdatesPage, count, impersonation, seen.isPending, seen.isError, seen.mutate]);

  if (!allowed) return null;
  return <button type="button" className="btn btn-ghost btn-icon-only btn-md notif-trigger" aria-label={count ? `Latest updates, ${count} new` : 'Latest updates'} title="Latest updates" onClick={() => (navigateTo ?? navigate)(destination)}>
    <Sparkles size={18} aria-hidden />
    {count > 0 && <span className="notif-dot tabular" aria-hidden>{count > 9 ? '9+' : count}</span>}
  </button>;
}
